/**
 * Player Store
 * ------------
 * Central state for the video player (both HLS and Guacamole modes).
 *
 * Responsibilities:
 *  - Playback state (playing, loading, error, currentTime, duration)
 *  - Zoom and pan transform
 *  - User preferences (persisted to localStorage)
 *  - Timeline heatmap data (fetched once per session, cached in memory)
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import { useSessionStore } from "./sessionStore";
import { getSessionTimeline } from "../services/timelineService";

export const usePlayerStore = defineStore("playerStore", () => {

  // -------------------------------------------------------------------------
  // User preferences (persisted)
  // -------------------------------------------------------------------------

  const PREF = "player_";

  const saveProgressEnabled = ref(
    localStorage.getItem(PREF + "saveProgress") !== "false"
  );
  const autoPlayEnabled = ref(
    localStorage.getItem(PREF + "autoPlay") !== "false"
  );
  const timestampsEnabled = ref(
    localStorage.getItem(PREF + "timestamps") !== "false"
  );

  function toggleSaveProgress() {
    saveProgressEnabled.value = !saveProgressEnabled.value;
    localStorage.setItem(PREF + "saveProgress", saveProgressEnabled.value);
  }

  function toggleAutoPlay() {
    autoPlayEnabled.value = !autoPlayEnabled.value;
    localStorage.setItem(PREF + "autoPlay", autoPlayEnabled.value);
  }

  function toggleTimestamps() {
    timestampsEnabled.value = !timestampsEnabled.value;
    localStorage.setItem(PREF + "timestamps", timestampsEnabled.value);
  }

  // -------------------------------------------------------------------------
  // Playback state
  // -------------------------------------------------------------------------

  /** True when the Guacamole player is active (as opposed to HLS) */
  const isGuacMode = ref(false);

  /**
   * True during Guacamole player initialization (seeking to frame 0).
   * Activity data is not loaded while this is true to avoid race conditions.
   */
  const isInitializing = ref(false);

  const isPlaying  = ref(false);
  const loading    = ref(false);
  const error      = ref(null);

  const currentTime = ref(0);
  const duration    = ref(0);
  const bufferedPct = ref(0);

  const resolution     = ref(null);
  const codec          = ref(null);
  const currentBitrate = ref(null);
  const droppedFrames  = ref(0);

  // -------------------------------------------------------------------------
  // Zoom & Pan
  // -------------------------------------------------------------------------

  const scale = ref(1);
  const posX  = ref(0);
  const posY  = ref(0);

  function resetZoom() {
    scale.value = 1;
    posX.value  = 0;
    posY.value  = 0;
  }

  // -------------------------------------------------------------------------
  // Playback state setters
  // -------------------------------------------------------------------------

  function setPlaying(v)       { isPlaying.value = v; }
  function setLoading(v)       { loading.value = v; }
  function setLoaded()         { loading.value = false; error.value = null; }
  function setError(msg)       { error.value = msg; loading.value = false; isPlaying.value = false; }
  function setCurrentTime(t)   { currentTime.value = t; }
  function setDuration(d)      { duration.value = d; }
  function setBufferedPct(pct) { bufferedPct.value = pct; }
  function setResolution(w, h) { resolution.value = `${w}x${h}`; }
  function setCodec(c)         { codec.value = c; }
  function setBitrate(b)       { currentBitrate.value = b; }
  function setDroppedFrames(n) { droppedFrames.value = n; }

  // -------------------------------------------------------------------------
  // Timeline / activity heatmap
  // -------------------------------------------------------------------------

  const activityHeatmap  = ref([]);
  const activityLoading  = ref(false);
  const activityError    = ref(null);
  const activityGaps     = ref([]);
  const activityPeriods  = ref([]);
  const activityMarkers  = ref([]);   // NEW: detection / command / keystroke markers
  const activityStats    = ref(null);

  // -------------------------------------------------------------------------
  // Per-session analysis cache
  //
  // Prevents re-fetching timeline data when the user switches between HLS
  // and Guacamole modes for the same session. Cache is keyed by session id
  // and cleared when the user selects a different session.
  // -------------------------------------------------------------------------

  const _cache = ref(new Map());

  function _cacheSet(sessionId, data) {
    _cache.value.set(sessionId, { ...data, cachedAt: Date.now() });
  }

  function getCachedAnalysis(sessionId) {
    return _cache.value.get(sessionId) ?? null;
  }

  function clearCacheForSession(sessionId) {
    _cache.value.delete(sessionId);
  }

  function clearAllCache() {
    _cache.value.clear();
  }

  // -------------------------------------------------------------------------
  // Load activity / timeline data
  //
  // Called by ProgressBar and ActivityTab when a session is selected and the
  // player duration becomes known.
  //
  // The new Watch API endpoint returns duration_ms itself, so we no longer
  // need the caller to pass the video duration — but we keep the parameter
  // for backwards compatibility with ProgressBar.vue which still passes it.
  // -------------------------------------------------------------------------

  let _loadDebounce = null;

  /**
   * Fetches the timeline for the current session and populates the activity
   * state. Results are cached in memory for the session lifetime.
   *
   * @param {string}  sessionId
   * @param {number}  [_duration]  - Ignored (duration comes from the API now)
   * @param {boolean} [forceReload=false]
   */
  async function loadActivityData(sessionId, _duration, forceReload = false) {
    if (isInitializing.value) {
      console.log("[playerStore] Skipping activity load — player initializing");
      return;
    }

    if (activityLoading.value) return;

    // Return cached data if available
    if (!forceReload) {
      const cached = getCachedAnalysis(sessionId);
      if (cached) {
        activityHeatmap.value = cached.heatmap;
        activityGaps.value    = cached.gaps;
        activityPeriods.value = cached.periods;
        activityMarkers.value = cached.markers;
        activityStats.value   = cached.stats;
        return;
      }
    }

    // Debounce rapid calls (e.g. duration watcher firing multiple times)
    if (_loadDebounce) clearTimeout(_loadDebounce);

    _loadDebounce = setTimeout(async () => {
      activityLoading.value = true;
      activityError.value   = null;

      try {
        const result = await getSessionTimeline(sessionId);

        activityHeatmap.value = result.heatmap;
        activityGaps.value    = result.gaps    ?? [];
        activityPeriods.value = result.periods ?? [];
        activityMarkers.value = result.markers ?? [];
        activityStats.value   = result.stats   ?? null;

        _cacheSet(sessionId, {
          heatmap: result.heatmap,
          gaps:    result.gaps    ?? [],
          periods: result.periods ?? [],
          markers: result.markers ?? [],
          stats:   result.stats   ?? null,
        });

        console.log("[playerStore] Timeline loaded:", {
          buckets:  result.heatmap.length,
          markers:  result.markers?.length ?? 0,
          duration: result.stats?.durationSeconds,
        });
      } catch (err) {
        console.error("[playerStore] Timeline load failed:", err);
        activityError.value   = err.message;
        activityHeatmap.value = [];
        activityGaps.value    = [];
        activityPeriods.value = [];
        activityMarkers.value = [];
        activityStats.value   = null;
      } finally {
        activityLoading.value = false;
      }
    }, 400);
  }

  /**
   * Clears all activity data. Called when switching to a new session.
   */
  function clearActivityData() {
    if (_loadDebounce) {
      clearTimeout(_loadDebounce);
      _loadDebounce = null;
    }

    activityHeatmap.value = [];
    activityGaps.value    = [];
    activityPeriods.value = [];
    activityMarkers.value = [];
    activityStats.value   = null;
    activityError.value   = null;
  }

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  return {
    // Preferences
    saveProgressEnabled,
    autoPlayEnabled,
    timestampsEnabled,
    toggleSaveProgress,
    toggleAutoPlay,
    toggleTimestamps,

    // Playback state
    isGuacMode,
    isInitializing,
    isPlaying,
    loading,
    error,
    currentTime,
    duration,
    bufferedPct,
    resolution,
    codec,
    currentBitrate,
    droppedFrames,

    // Zoom & Pan
    scale,
    posX,
    posY,
    resetZoom,

    // Setters
    setPlaying,
    setLoading,
    setLoaded,
    setError,
    setCurrentTime,
    setDuration,
    setBufferedPct,
    setResolution,
    setCodec,
    setBitrate,
    setDroppedFrames,

    // Activity / timeline
    activityHeatmap,
    activityLoading,
    activityError,
    activityGaps,
    activityPeriods,
    activityMarkers,
    activityStats,
    loadActivityData,
    clearActivityData,

    // Cache
    getCachedAnalysis,
    clearCacheForSession,
    clearAllCache,
  };
});